import React, { useState, useMemo, useEffect, useCallback, useDeferredValue } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Pagination,
  useTheme,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Chip,
  TableSortLabel,
} from '@mui/material';
import { BglPrimaryButton, BglPrimaryOutlinedSmallButton, BglPrimarySmallButton } from 'components/button/BglButtons';
import { BglOverlayLoader } from 'components/loading';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useToast } from 'context/ToastContext';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId ,selectPilotList} from 'store/dataReducers/pilotsSlice';
import { PilotApis } from 'apis/pilotAPI';
import { ClusterApis } from 'apis/clusterAPI';
import { IngestionConfig, SaveProjectConfigData, UiFilters } from 'types/apis/pilotInterfaces';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import CodeIcon from '@mui/icons-material/Code';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
// removed autosize import; using TextField multiline instead
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { BglSelect } from 'components/common/BglSelect';
import { ProjectConfigFilter, ProjectConfigFilterActiveChips } from 'features/pilot/ProjectConfigFilter';
import { getLevelDisplayForConfig } from 'features/config-tools/utils/levelDisplay';
import { StudioModal } from 'features/environments/StudioModal';
import studioStyles from 'features/environments/StudioModal.module.scss';

const ROWS_PER_PAGE_OPTIONS = [50, 100, 200, 500];

type SortableColumn = 'configType' | 'configKey' | 'component' | 'configLevel';

/** Normalize raw API config items to IngestionConfig[]. Reusable for single or merged responses. */
function normalizeRawConfigs(raw: unknown[]): IngestionConfig[] {
  return raw
    .filter((cfg): cfg is Record<string, unknown> => cfg != null && typeof cfg === 'object' && !Array.isArray(cfg))
    .map((cfg, index) => {
      const meta = (cfg.metadata as Record<string, unknown> | undefined);
      const configKey = (cfg.configKey ?? cfg.config_key ?? meta?.configKey ?? meta?.config_key) as string | undefined;
      const id = (cfg.id as string | undefined) ?? (configKey ? `${String(configKey)}__${meta?.level_display ?? meta?.level ?? cfg.level_display ?? cfg.level ?? index}` : `config_${index}`);
      const productType = (cfg.productType ?? cfg.product_type ?? meta?.productType ?? meta?.product_type) as string | undefined;
      const fuelType = (cfg.fuelType ?? cfg.fuel_type ?? meta?.fuelType ?? meta?.fuel_type) as string | undefined;
      const levelDisplay = (cfg.level_display ?? meta?.level_display ?? meta?.levelDisplay) as string | undefined;
      // Prefer explicit config level fields over generic `level` — API often sends both; `level` can be a default
      // (e.g. pilot) while configLevel is the real scope (e.g. global), which broke override level options.
      const level = (cfg.configLevel ??
        cfg.config_level ??
        (meta?.configLevel as string | undefined) ??
        (meta?.config_level as string | undefined) ??
        cfg.level ??
        meta?.level) as string | undefined;
      const editableFromApi = (cfg as any).editable ?? (meta as any)?.editable;
      return {
        ...(cfg as Partial<IngestionConfig>),
        id: id || `config_${index}`,
        configKey: configKey ?? '',
        configType: String(cfg.configType ?? cfg.config_type ?? meta?.configType ?? meta?.config_type ?? ''),
        displayName: String(cfg.displayName ?? cfg.display_name ?? meta?.displayName ?? meta?.display_name ?? ''),
        configLevel: (level ?? 'pilot') as IngestionConfig['configLevel'],
        defaultValue: String(cfg.defaultValue ?? cfg.default_value ?? meta?.defaultValue ?? meta?.default_value ?? ''),
        productType: productType ?? null,
        fuelType: fuelType ?? null,
        clusterReference: (cfg.clusterReference ?? cfg.cluster_reference ?? meta?.clusterReference) as string | undefined ?? null,
        level: level ?? null,
        level_display: levelDisplay ?? null,
        metadata: { ...(meta || {}), level_display: levelDisplay ?? meta?.level_display, level: level ?? meta?.level } as any,
        editable: editableFromApi === false ? false : true,
      } as IngestionConfig;
    })
    .filter((cfg) => Boolean(cfg.configKey));
}

type ConfigLevelKind = 'global' | 'pilot' | 'product_type' | 'fuel_type' | 'cluster';

/** Override dialog targets only — global is never selectable as an override level. */
type OverrideTargetLevel = Exclude<ConfigLevelKind, 'global'>;

/** Normalize config level to one of the known level kinds. */
function getConfigLevelKind(cfg: IngestionConfig): ConfigLevelKind {
  const levelDisplayRaw = (
    cfg.level_display ??
    cfg.metadata?.level_display ??
    (cfg.metadata as any)?.levelDisplay ??
    ''
  )
    .toString()
    .trim()
    .toLowerCase();
  const isGlobalDisplay = levelDisplayRaw === 'global' || levelDisplayRaw.startsWith('global.');

  const raw = (
    cfg?.configLevel ??
    (cfg as any)?.config_level ??
    (cfg as any)?.level ??
    cfg?.metadata?.level ??
    ''
  )
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  if (raw === 'global') return 'global';
  if (raw === 'product_type' || raw === 'producttype') return 'product_type';
  if (raw === 'fuel_type' || raw === 'fueltype') return 'fuel_type';
  if (raw === 'cluster') return 'cluster';
  // API sometimes sends GLOBAL only in level_display while configLevel stays default pilot
  if (isGlobalDisplay) return 'global';
  return 'pilot';
}

/**
 * Levels the user may override *onto*. Global is never a valid target.
 * From a global row, every non-global level (including Pilot) is allowed.
 * From any other row, the same level as the source row is not offered.
 */
function getOverrideLevelOptions(
  sourceLevel: ConfigLevelKind,
  allOptions: { value: OverrideTargetLevel; label: string }[]
): { value: OverrideTargetLevel; label: string }[] {
  return allOptions.filter((opt) => {
    if (sourceLevel === 'global') return true;
    return opt.value !== sourceLevel;
  });
}

/** True when this row is a global-level config (not editable inline; use Override at lower levels). */
function isGlobalLevelConfig(cfg: IngestionConfig): boolean {
  return getConfigLevelKind(cfg) === 'global';
}

const GLOBAL_CONFIG_NON_EDITABLE_HINT =
  'Global configs are not editable here. To update this value, use Override to set it at pilot, application, fuel, or cluster level.';

/** Display/validation from metadata when present (new API shape), else top-level (legacy). */
function getConfigDisplay(cfg: IngestionConfig) {
  const m = cfg.metadata;
  const levelDisplay = (cfg.level_display ?? m?.level_display ?? (m as any)?.levelDisplay ?? '').trim();
  return {
    displayName: (m?.displayName ?? cfg.displayName) ?? '',
    component: (m?.component ?? (cfg as any).component) ?? '',
    configDocumentation: (m?.configDocumentation ?? cfg.configDocumentation) ?? '',
    configRegex: (m?.configRegex ?? cfg.configRegex) ?? '.*',
    configDataType: (m?.configDataType ?? cfg.configDataType) ?? '',
    editable: m?.editable ?? cfg.editable ?? false,
    defaultValue: (m?.defaultValue != null && m.defaultValue !== 'null' ? String(m.defaultValue) : cfg.defaultValue) ?? '',
    possibleValues: (m?.possibleValues ?? cfg.possibleValues) ?? '',
    features: (m?.features ?? cfg.features) ?? 'ingestion',
    /** Level kind from metadata or top-level (product_type, fuel_type, pilot, cluster, etc.) */
    level: (cfg.level ?? m?.level ?? '').trim(),
    /** Scope value; pass this in save API payload as level_display */
    levelDisplay: levelDisplay || undefined,
  };
}

/** Metadata pointers to configs that should stay in sync (duplicate / related pair). */
type DuplicateConfigRef = { configKey: string; configType: string };

function parseDuplicateRefsFromMetadata(meta: Record<string, unknown> | null | undefined): DuplicateConfigRef[] {
  const out: DuplicateConfigRef[] = [];
  const push = (k: unknown, t: unknown) => {
    if (k == null || String(k).trim() === '') return;
    out.push({ configKey: String(k).trim(), configType: String(t ?? '').trim() });
  };
  if (!meta || typeof meta !== 'object') return [];
  const dc = (meta as { duplicateConfig?: unknown }).duplicateConfig;
  if (dc != null && typeof dc === 'object') {
    if (Array.isArray(dc)) {
      for (const item of dc) {
        if (item && typeof item === 'object') {
          const o = item as Record<string, unknown>;
          push(o.configKey ?? o.config_key, o.configType ?? o.config_type);
        }
      }
    } else {
      const o = dc as Record<string, unknown>;
      push(o.configKey ?? o.config_key, o.configType ?? o.config_type);
    }
  }
  const seen = new Set<string>();
  return out.filter((r) => {
    const key = `${r.configType}::${r.configKey}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function sameConfigScope(a: IngestionConfig, b: IngestionConfig): boolean {
  return (
    String(a.configLevel ?? '') === String(b.configLevel ?? '') &&
    String(a.productType ?? '') === String(b.productType ?? '') &&
    String(a.fuelType ?? '') === String(b.fuelType ?? '') &&
    String(a.clusterReference ?? (a as { clusterId?: string }).clusterId ?? '') ===
      String(b.clusterReference ?? (b as { clusterId?: string }).clusterId ?? '')
  );
}

/** Rows that metadata marks as duplicates of `cfg`, restricted to the same config level / scope. */
function findDirectDuplicateSiblings(cfg: IngestionConfig, allConfigs: IngestionConfig[]): IngestionConfig[] {
  const refs = parseDuplicateRefsFromMetadata(cfg.metadata as Record<string, unknown> | undefined);
  const matchKeys = new Set(refs.map((r) => `${r.configType}::${r.configKey}`));
  const myKey = `${cfg.configType}::${cfg.configKey}`;
  const found = new Map<string, IngestionConfig>();

  for (const c of allConfigs) {
    if (c.id === cfg.id || !sameConfigScope(cfg, c)) continue;
    const ck = `${c.configType}::${c.configKey}`;
    if (matchKeys.has(ck)) found.set(c.id, c);
  }
  for (const c of allConfigs) {
    if (c.id === cfg.id || !sameConfigScope(cfg, c)) continue;
    const rev = parseDuplicateRefsFromMetadata(c.metadata as Record<string, unknown> | undefined);
    for (const r of rev) {
      if (`${r.configType}::${r.configKey}` === myKey) found.set(c.id, c);
    }
  }
  return Array.from(found.values());
}

function buildDuplicatePartnerMap(allConfigs: IngestionConfig[]): Map<string, Set<string>> {
  const partners = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (a === b) return;
    if (!partners.has(a)) partners.set(a, new Set());
    if (!partners.has(b)) partners.set(b, new Set());
    partners.get(a)!.add(b);
    partners.get(b)!.add(a);
  };
  for (const c of allConfigs) {
    for (const s of findDirectDuplicateSiblings(c, allConfigs)) link(c.id, s.id);
  }
  return partners;
}

/** All other config ids in the same duplicate cluster (connected through metadata links). */
function getDuplicateClusterNeighborIds(id: string, partners: Map<string, Set<string>>): string[] {
  if (!partners.has(id)) return [];
  const visited = new Set<string>([id]);
  const q: string[] = [id];
  while (q.length) {
    const cur = q.shift()!;
    for (const p of partners.get(cur) ?? []) {
      if (!visited.has(p)) {
        visited.add(p);
        q.push(p);
      }
    }
  }
  return Array.from(visited).filter((x) => x !== id);
}

/** Only saved-but-not-pushed rows should float to top. */
function configRowIsEditedForSort(pendingPushMatch: boolean): boolean {
  return pendingPushMatch;
}

export const PilotConfigsPage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.CONFIGS_LOADED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [configs, setConfigs] = useState<IngestionConfig[]>([]);
  const [search, setSearch] = useState('');
  const [lastSaved, setLastSaved] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortBy, setSortBy] = useState<SortableColumn | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const deferredSortBy = useDeferredValue(sortBy);
  const deferredSortDirection = useDeferredValue(sortDirection);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Inline editing states (Studio-like UX)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const editingValueRef = React.useRef<string>('');
  const editingInputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});

  // Override dialog states (Studio-like Action column); levels match filters (Pilot, Application, Fuel, Cluster)
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideConfig, setOverrideConfig] = useState<IngestionConfig | null>(null);
  const [overrideLevel, setOverrideLevel] = useState<OverrideTargetLevel>('pilot');
  const [overrideCluster, setOverrideCluster] = useState<string>('');
  const [overrideProductType, setOverrideProductType] = useState<string>('');
  const [overrideFuelType, setOverrideFuelType] = useState<string>('');
  const [overrideValue, setOverrideValue] = useState<string>('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // View Override dialog: show existing overrides for a config key (loaded from API)
  const [viewOverrideOpen, setViewOverrideOpen] = useState(false);
  const [viewOverrideConfig, setViewOverrideConfig] = useState<IngestionConfig | null>(null);
  const [viewOverrideListFromApi, setViewOverrideListFromApi] = useState<IngestionConfig[]>([]);
  const [viewOverrideLoading, setViewOverrideLoading] = useState(false);
  const [viewOverrideError, setViewOverrideError] = useState<string | null>(null);

  // Filter states (all multi-select). Empty = show all (full API response). User selections narrow the list.
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [applicationFilter, setApplicationFilter] = useState<string[]>([]);
  const [fuelTypeFilter, setFuelTypeFilter] = useState<string[]>([]);
  const [clusterFilter, setClusterFilter] = useState<string[]>([]);
  const [configTypeFilter, setConfigTypeFilter] = useState<string[]>([]);
  const [configKeyFilter, setConfigKeyFilter] = useState<string[]>([]);
  const [componentFilter, setComponentFilter] = useState<string[]>([]);
  // Cluster options: value = cluster id from API (for cluster_id in save); label = display; clusterReference = for list filter API
  const [clusters, setClusters] = useState<Array<{ value: string; label: string; clusterReference?: string }>>([]);
  const [clusterLoading, setClusterLoading] = useState(false);
  /** From configs response ui_filters; when set, dropdowns and clusters come from here (no getClusters). */
  const [uiFilters, setUiFilters] = useState<UiFilters | null>(null);
  const uniqueConfigTypes = useMemo(() => {
    if (!Array.isArray(configs)) return [];
    const set = new Set<string>();
    configs.forEach((c) => {
      if (c?.configType && String(c.configType).trim()) set.add(String(c.configType).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [configs]);

  const uniqueComponents = useMemo(() => {
    if (!Array.isArray(configs)) return [];
    const set = new Set<string>();
    configs.forEach((c) => {
      const comp = getConfigDisplay(c).component;
      if (comp && String(comp).trim()) set.add(String(comp).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [configs]);

  const uniqueConfigKeys = useMemo(() => {
    if (!Array.isArray(configs)) return [];
    const set = new Set<string>();
    configs.forEach((c) => {
      const key = c?.configKey ?? (c as any)?.config_key;
      if (key != null && String(key).trim()) set.add(String(key).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [configs]);

  /** Map raw configLevel (from API) to display category for Level filter. */
  const configLevelToCategory = useCallback((raw: string): string => {
    const level = (raw ?? 'pilot').toString().toLowerCase();
    if (level === 'global') return 'GLOBAL';
    if (level === 'pilot') return 'PILOT';
    if (level === 'product_type' || level === 'producttype') return 'APPLICATIONS';
    if (level === 'fuel_type' || level === 'fueltype') return 'FUEL';
    if (level === 'cluster') return 'CLUSTERS';
    return 'PILOT';
  }, []);

  /**
   * Level filter options: union of (1) distinct configLevel from loaded configs and (2) dimensions
   * exposed in ui_filters so APPLICATIONS / FUEL / CLUSTERS appear even when the current payload
   * has no rows at that level yet.
   */
  const levelDisplayOptions = useMemo(() => {
    const categoryOrder = ['GLOBAL', 'PILOT', 'APPLICATIONS', 'FUEL', 'CLUSTERS'];
    const set = new Set<string>();

    if (Array.isArray(configs) && configs.length > 0) {
      configs.forEach((c) => {
        const raw = (c.level ?? c.configLevel ?? (c as any)?.config_level ?? 'pilot').toString().trim();
        set.add(configLevelToCategory(raw));
      });
    }

    if (uiFilters?.applications?.length) set.add('APPLICATIONS');
    if (uiFilters?.fuel?.length) set.add('FUEL');
    if (uiFilters?.clusters?.length) set.add('CLUSTERS');

    return categoryOrder.filter((cat) => set.has(cat));
  }, [configs, configLevelToCategory, uiFilters]);

  /** Application (product type) options: from ui_filters.applications when present, else derived from configs (per §2.3). */
  const applicationOptions = useMemo((): Array<{ value: string; label: string }> => {
    if (uiFilters?.applications?.length) {
      return uiFilters.applications.map((p) => ({ value: p.id, label: p.name }));
    }
    if (!Array.isArray(configs)) return [];
    const set = new Set<string>();
    configs.forEach((c) => {
      const pt = c.productType ?? (c as any).product_type ?? (c.metadata as any)?.productType ?? (c.metadata as any)?.product_type;
      if (pt != null && String(pt).trim() !== '') set.add(String(pt).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((a) => ({ value: a, label: a }));
  }, [configs, uiFilters]);

  /** When false, do not apply application filter and show hint (doc §2.3). When ui_filters present use applications length. */
  const applicationScopingAvailable = useMemo(
    () => uiFilters
      ? (uiFilters.applications?.length ?? 0) > 0
      : configs.some((c) => (c.productType ?? (c as any).product_type ?? (c.metadata as any)?.productType ?? (c.metadata as any)?.product_type) != null &&
          String((c.productType ?? (c as any).product_type ?? (c.metadata as any)?.productType ?? (c.metadata as any)?.product_type)).trim() !== ''),
    [configs, uiFilters]
  );

  /** Fuel type options: from ui_filters.fuel when present, else derived from configs. */
  const fuelTypeOptions = useMemo((): Array<{ value: string; label: string }> => {
    if (uiFilters?.fuel?.length) {
      return uiFilters.fuel.map((f) => ({ value: f.id, label: f.name }));
    }
    if (!Array.isArray(configs)) return [];
    const set = new Set<string>();
    configs.forEach((c) => {
      const meta = c.metadata as Record<string, unknown> | undefined;
      const ft = (c.fuelType ?? (c as any).fuel_type ?? meta?.fuelType ?? meta?.fuel_type) as string | undefined;
      if (ft != null && String(ft).trim() !== '') set.add(String(ft).trim());
      const d = getConfigDisplay(c);
      if (d.level === 'fuel_type' && d.levelDisplay) set.add(d.levelDisplay.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((f) => ({ value: f, label: f }));
  }, [configs, uiFilters]);

  const projectFilterOptions = useMemo(
    () => ({
      levels: levelDisplayOptions,
      clusters,
      applications: [
        { value: '', label: 'All' },
        ...applicationOptions,
      ],
      fuelTypes: [
        { value: 'All', label: 'All' },
        ...fuelTypeOptions,
      ],
      configTypes: uniqueConfigTypes,
      configKeys: uniqueConfigKeys,
      components: uniqueComponents,
    }),
    [levelDisplayOptions, clusters, applicationOptions, fuelTypeOptions, uniqueConfigTypes, uniqueConfigKeys, uniqueComponents]
  );

  const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
  const pilotIdForApi = selectedPilot?.projectId || '';

  type ConfigsApiParams = { level?: string; levels?: string[]; productType?: string; fuelType?: string; cluster_reference?: string; cluster_id?: string };

  /** Single "get all" request: all levels, no scope filters. All filtering is done on the client. */
  const GET_ALL_CONFIGS_PARAMS: ConfigsApiParams = useMemo(
    () => ({ levels: ['global', 'pilot', 'product_type', 'fuel_type', 'cluster'] }),
    []
  );

  /** Which scope the user is editing (for save payload and chips). Filtering is client-side only. */
  const effectiveApiScope = useMemo((): 'cluster' | 'product_type' | 'fuel_type' | 'pilot' => {
    if (clusterFilter.length > 0 && clusterFilter[0]?.trim()) return 'cluster';
    if (applicationFilter.length > 0 && applicationFilter[0]?.trim()) return 'product_type';
    if (fuelTypeFilter.length > 0 && fuelTypeFilter[0]?.trim()) return 'fuel_type';
    return 'pilot';
  }, [clusterFilter, applicationFilter, fuelTypeFilter]);

  /**
   * Builds a single save-payload item for saveProjectConfigs.
   * When a cluster is selected (effectiveApiScope === 'cluster'), uses filter-derived scope so the
   * payload targets cluster.application.fueltype even if the backend did not set configLevel/clusterReference on each config.
   * When no cluster is selected, keeps current behavior (config_level and cluster fields from config only).
   */
  const buildOneSavePayload = useCallback(
    (cfg: IngestionConfig, configValue: string): SaveProjectConfigData => {
      const d = getConfigDisplay(cfg);
      const base = {
        project_id: pilotIdForApi,
        config_key: cfg.configKey,
        config_type: cfg.configType,
        config_value: configValue,
        config_documentation: d.configDocumentation || '',
        config_regex: d.configRegex || '.*',
        config_data_type: d.configDataType || '',
        display_name: d.displayName,
        features: d.features || 'ingestion',
        editable: d.editable,
        is_required: true,
      };

      if (effectiveApiScope === 'cluster' && clusterFilter[0]?.trim()) {
        const clusterId = clusterFilter[0].trim();
        const productType = applicationFilter[0]?.trim() || (cfg.productType ?? (cfg as any).product_type ?? '') || '';
        const fuelType = fuelTypeFilter[0]?.trim() || (cfg.fuelType ?? (cfg as any).fuel_type ?? '') || '';
        const clusterLabel = clusters.find((c) => c.value === clusterId)?.label ?? clusterId;
        const levelDisplay = [clusterLabel, productType || undefined, fuelType || undefined].filter(Boolean).join('.');
        return {
          ...base,
          config_level: 'cluster',
          cluster_id: clusterId,
          product_type: productType,
          fuel_type: fuelType,
          ...(levelDisplay ? { level_display: levelDisplay } : {}),
        };
      }

      const level = cfg.configLevel || 'pilot';
      const pt = cfg.productType ?? (cfg as any).product_type ?? '';
      const ft = cfg.fuelType ?? (cfg as any).fuel_type ?? '';

      const scopeFields: Record<string, string> = {};
      if (level === 'cluster' && cfg.clusterReference) {
        scopeFields.cluster_id = cfg.clusterReference;
        scopeFields.product_type = pt;
        scopeFields.fuel_type = ft;
      } else if (level === 'fuel_type') {
        scopeFields.product_type = pt;
        scopeFields.fuel_type = ft;
      } else if (level === 'product_type') {
        scopeFields.product_type = pt;
      }

      return {
        ...base,
        config_level: level,
        ...(d.levelDisplay ? { level_display: d.levelDisplay } : {}),
        ...scopeFields,
      };
    },
    [pilotIdForApi, effectiveApiScope, clusterFilter, applicationFilter, fuelTypeFilter, clusters]
  );

  const fetchConfigs = useCallback(async (apiParams?: ConfigsApiParams) => {
    if (!pilotIdForApi) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await PilotApis.getIngestionConfigs(rc, pilotIdForApi, apiParams);

      if (response.success && response.data != null) {
        // Store ui_filters when present (dropdown options only). Do not set any filters — show full response by default.
        if (response.uiFilters != null) {
          setUiFilters(response.uiFilters);
          const uf = response.uiFilters;
          if (uf.clusters && uf.clusters.length > 0) {
            const clusterOptions: Array<{ value: string; label: string; clusterReference?: string }> = uf.clusters
              .map((c) => ({
                value: (c.id ?? c.clusterReference ?? '').toString(),
                label: (c.clusterReference ?? c.id ?? '').toString(),
                clusterReference: c.clusterReference,
              }))
              .filter((c) => Boolean(c.value) && c.value !== 'DEFAULT' && Boolean(c.label));
            setClusters(clusterOptions);
          } else {
            setClusters([]);
          }
        } else {
          setUiFilters(null);
          setClusters([]);
        }

        // Backend contract: { configs: [...] }; API layer returns the array (or []). Treat as valid even if empty.
        const raw: unknown[] = Array.isArray(response.data) ? response.data : [];
        const validConfigs = normalizeRawConfigs(raw);

        setConfigs(validConfigs);
        setLastSaved(validConfigs.map(cfg => cfg.defaultValue || ''));
        setPage(1);
        if (validConfigs.length === 0 && raw.length > 0) {
          setError('No valid configurations found in response (missing configKey).');
        } else {
          setError(null);
        }
      } else {
        const errMsg =
          typeof response.error === 'string'
            ? response.error
            : (response.error as { message?: string } | undefined)?.message ?? 'Failed to fetch configurations';
        setError(errMsg);
        setConfigs([]);
        setLastSaved([]);
        setPage(1);
      }
    } catch (err) {
      setError('Failed to fetch configurations');
      setConfigs([]);
      setLastSaved([]);
      setPage(1);
    } finally {
      setIsLoading(false);
    }
  }, [rc, pilotIdForApi]);

  useEffect(() => {
    if (!pilotIdForApi) return;
    fetchConfigs(GET_ALL_CONFIGS_PARAMS);
  }, [pilotIdForApi, fetchConfigs, GET_ALL_CONFIGS_PARAMS]);

  const duplicatePartnerMap = useMemo(() => buildDuplicatePartnerMap(configs), [configs]);
  const duplicateClusterOthersById = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const c of configs) {
      const others = getDuplicateClusterNeighborIds(c.id, duplicatePartnerMap);
      if (others.length > 0) m.set(c.id, others);
    }
    return m;
  }, [configs, duplicatePartnerMap]);

  const filteredConfigs = useMemo(() => {
    if (!Array.isArray(configs)) return [];

    let filtered = configs;

    const levelCategories = new Set(['GLOBAL', 'PILOT', 'APPLICATIONS', 'FUEL', 'CLUSTERS']);
    const allLevelsAreCategories = levelFilter.length > 0 && levelFilter.every((l) => levelCategories.has(l.toUpperCase()));

    if (levelFilter.length > 0) {
      if (allLevelsAreCategories) {
        const levelSet = new Set(levelFilter.map((l) => l.toUpperCase()));
        filtered = filtered.filter((cfg) => {
          const cfgLevel = (cfg.level ?? cfg.configLevel ?? 'pilot').toString().toLowerCase();
          const category =
            cfgLevel === 'global'
              ? 'GLOBAL'
              : cfgLevel === 'pilot'
                ? 'PILOT'
                : cfgLevel === 'product_type'
                  ? 'APPLICATIONS'
                  : cfgLevel === 'fuel_type'
                    ? 'FUEL'
                    : cfgLevel === 'cluster'
                      ? 'CLUSTERS'
                      : null;
          return category != null && levelSet.has(category);
        });
      } else {
        const levelSet = new Set(levelFilter);
        filtered = filtered.filter((cfg) => {
          const ld = getLevelDisplayForConfig(cfg, clusters);
          return ld && levelSet.has(ld);
        });
      }
    }

    // Apply scope filters per config level: cluster/product_type/fuel_type configs restricted to selected scope; pilot configs kept as-is.
    if (clusterFilter.length > 0) {
      const clusterSet = new Set<string>(clusterFilter);
      clusters.forEach((c) => {
        if (clusterSet.has(c.value)) {
          if (c.clusterReference) clusterSet.add(c.clusterReference);
          if (c.label) clusterSet.add(c.label);
        }
      });
      filtered = filtered.filter(
        (cfg) =>
          cfg.configLevel !== 'cluster' ||
          (cfg.clusterReference != null && clusterSet.has(cfg.clusterReference)) ||
          ((cfg as any).clusterId != null && clusterSet.has((cfg as any).clusterId))
      );
    }
    if (applicationFilter.length > 0 && applicationScopingAvailable) {
      const appSet = new Set(applicationFilter.map((a) => a.trim()).filter(Boolean));
      filtered = filtered.filter((cfg) => {
        if ((cfg.configLevel ?? (cfg as any).config_level) !== 'product_type') return true;
        const pt = (cfg.productType ?? (cfg as any).product_type ?? '').toString().trim();
        return pt && appSet.has(pt);
      });
    }
    if (fuelTypeFilter.length > 0) {
      const fuelSet = new Set(fuelTypeFilter.map((f) => f.trim().toUpperCase()));
      filtered = filtered.filter((cfg) => {
        if ((cfg.configLevel ?? (cfg as any).config_level) !== 'fuel_type') return true;
        return fuelSet.has((cfg.fuelType ?? (cfg as any).fuel_type ?? '').toString().trim().toUpperCase());
      });
    }

    if (configTypeFilter.length > 0) {
      const typeSet = new Set(configTypeFilter.map((t) => t.trim().toLowerCase()));
      filtered = filtered.filter(
        (cfg) => cfg?.configType && typeSet.has(String(cfg.configType).trim().toLowerCase())
      );
    }

    if (componentFilter.length > 0) {
      const compSet = new Set(componentFilter.map((c) => c.trim().toLowerCase()));
      filtered = filtered.filter((cfg) => {
        const d = getConfigDisplay(cfg);
        const comp = (d.component ?? '').trim().toLowerCase();
        return comp && compSet.has(comp);
      });
    }

    if (configKeyFilter.length > 0) {
      const keySet = new Set(configKeyFilter.map((k) => k.trim().toLowerCase()));
      filtered = filtered.filter((cfg) => {
        const key = (cfg?.configKey ?? (cfg as any)?.config_key ?? '').toString().trim().toLowerCase();
        return key && keySet.has(key);
      });
    }

    if (search.trim()) {
      const s = search.trim().toLowerCase();
      filtered = filtered.filter((cfg) => {
        if (!cfg || typeof cfg !== 'object') return false;
        const d = getConfigDisplay(cfg);
        const str = (v: unknown) => (v != null ? String(v).toLowerCase() : '');
        const configKey = str(cfg.configKey ?? (cfg as any).config_key);
        const configType = str(cfg.configType ?? (cfg as any).config_type);
        const displayName = str(d.displayName);
        const configValue = str(cfg.defaultValue ?? (cfg as any).default_value);
        return configKey.includes(s) || configType.includes(s) || displayName.includes(s) || configValue.includes(s);
      });
    }

    return filtered;
  }, [configs, search, configTypeFilter, configKeyFilter, componentFilter, levelFilter, applicationFilter, fuelTypeFilter, clusterFilter, clusters, applicationScopingAvailable]);

  const sortedConfigs = useMemo(() => {
    if (!Array.isArray(filteredConfigs)) return [];
    const searchTerm = search.trim().toLowerCase();
    const cmp = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    const n = filteredConfigs.length;
    if (n === 0) return [];
    const getSortKey = (cfg: IngestionConfig): string => {
      if (deferredSortBy === 'configType') return (cfg.configType ?? '').toString().toLowerCase();
      if (deferredSortBy === 'configKey') return (cfg.configKey ?? (cfg as any).config_key ?? '').toString().toLowerCase();
      if (deferredSortBy === 'component') return (getConfigDisplay(cfg).component ?? '').toString().toLowerCase();
      if (deferredSortBy === 'configLevel') return (getLevelDisplayForConfig(cfg, clusters) ?? getConfigDisplay(cfg).levelDisplay ?? cfg.configLevel ?? '').toString().toLowerCase();
      return '';
    };
    const getRelevance = (cfg: IngestionConfig): number => {
      if (!searchTerm) return 0;
      const d = getConfigDisplay(cfg);
      const key = (cfg.configKey ?? (cfg as any).config_key ?? '').toString().toLowerCase();
      const displayName = (d.displayName ?? '').toString().toLowerCase();
      const configType = (cfg.configType ?? (cfg as any).config_type ?? '').toString().toLowerCase();
      const configValue = (cfg.defaultValue ?? (cfg as any).default_value ?? '').toString().toLowerCase();
      if (key === searchTerm || displayName === searchTerm) return 3;
      if (key.startsWith(searchTerm) || displayName.startsWith(searchTerm) || configType.startsWith(searchTerm) || configValue.startsWith(searchTerm)) return 2;
      if (key.includes(searchTerm) || displayName.includes(searchTerm) || configType.includes(searchTerm) || configValue.includes(searchTerm)) return 1;
      return 0;
    };
    const withKeys: { cfg: IngestionConfig; sortKey: string; relevance: number; edited: boolean }[] = new Array(n);
    for (let i = 0; i < n; i++) {
      const cfg = filteredConfigs[i];
      withKeys[i] = {
        cfg,
        sortKey: deferredSortBy ? getSortKey(cfg) : '',
        relevance: getRelevance(cfg),
        edited: configRowIsEditedForSort(cfg.isPushedToDataServer === false),
      };
    }
    const mult = deferredSortDirection === 'asc' ? 1 : -1;
    withKeys.sort((x, y) => {
      if (x.edited !== y.edited) return x.edited ? -1 : 1;
      if (searchTerm && x.relevance !== y.relevance) return y.relevance - x.relevance;
      if (!deferredSortBy) return 0;
      return mult * cmp(x.sortKey, y.sortKey);
    });
    return withKeys.map((x) => x.cfg);
  }, [
    filteredConfigs,
    deferredSortBy,
    deferredSortDirection,
    clusters,
    search,
    configs,
    lastSaved,
    pendingEdits,
    editingId,
  ]);

  const paginatedConfigs = useMemo(() => {
    if (!Array.isArray(sortedConfigs)) return [];
    const start = (page - 1) * rowsPerPage;
    return sortedConfigs.slice(start, start + rowsPerPage);
  }, [sortedConfigs, page, rowsPerPage]);

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const isDirty = configs.some((cfg, i) => {
    const d = getConfigDisplay(cfg);
    if (!d.editable || isGlobalLevelConfig(cfg)) return false;
    const saved = lastSaved[i] ?? '';
    const current = String(
      pendingEdits[cfg.id] ?? lastSaved[i] ?? d.defaultValue ?? cfg.defaultValue ?? ''
    );
    return current !== saved;
  });

  const handleValueChange = (id: string, value: string) => {
    setConfigs(cfgs => cfgs.map(cfg => cfg.id === id ? { ...cfg, defaultValue: value } : cfg));
  };

  // Studio-like inline editing handlers
  const handleStartEditing = (cfg: IngestionConfig) => {
    const d = getConfigDisplay(cfg);
    if (!d.editable || isGlobalLevelConfig(cfg)) return;
    const cfgIndex = configs.findIndex(c => c.id === cfg.id);
    const initial = String(
      (pendingEdits[cfg.id] ?? (cfgIndex >= 0 ? lastSaved[cfgIndex] : undefined) ?? d.defaultValue ?? cfg.defaultValue) || ''
    );
    setEditingId(cfg.id);
    setEditingValue(initial);
    editingValueRef.current = initial;
  };

  const handleCancelEditing = () => {
    if (!editingId) {
      setEditingId(null);
      setEditingValue('');
      editingValueRef.current = '';
      return;
    }
    const clusterIds = [editingId, ...(duplicateClusterOthersById.get(editingId) ?? [])];
    setPendingEdits((prev) => {
      const next = { ...prev };
      for (const cid of clusterIds) delete next[cid];
      return next;
    });
    setConfigs((cfgs) =>
      cfgs.map((cfg) => {
        if (!clusterIds.includes(cfg.id)) return cfg;
        const idx = cfgs.findIndex((c) => c.id === cfg.id);
        const reverted = idx >= 0 ? (lastSaved[idx] ?? '') : String(cfg.defaultValue ?? '');
        return { ...cfg, defaultValue: String(reverted) };
      })
    );
    setEditingId(null);
    setEditingValue('');
    editingValueRef.current = '';
  };

  const handleSaveEditing = async (valueFromEvent?: string) => {
    if (!editingId) return;

    const valueToSave =
      valueFromEvent !== undefined
        ? valueFromEvent
        : (editingInputRef.current?.value !== undefined && editingInputRef.current?.value !== null)
          ? String(editingInputRef.current.value)
          : (editingValueRef.current ?? editingValue);

    const target = configs.find(cfg => cfg.id === editingId);
    if (!target) {
      setEditingId(null);
      setEditingValue('');
      return;
    }
    if (isGlobalLevelConfig(target)) {
      setEditingId(null);
      setEditingValue('');
      return;
    }

    try {
      const clusterIds = [editingId, ...(duplicateClusterOthersById.get(editingId) ?? [])];
      const saveTargets = clusterIds
        .map((id) => configs.find((c) => c.id === id))
        .filter((c): c is IngestionConfig => c != null && !isGlobalLevelConfig(c));
      const savedVal = String(valueToSave ?? '');
      const configData = saveTargets.map((c) => buildOneSavePayload(c, savedVal));

      const result = await PilotApis.saveProjectConfigs(rc, configData);
      if (result.success) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.CONFIGS_SAVED, { config_count: saveTargets.length });
        const savedIds = new Set(saveTargets.map((c) => c.id));
        setConfigs((cfgs) =>
          cfgs.map((cfg) =>
            savedIds.has(cfg.id)
              ? {
                  ...cfg,
                  defaultValue: savedVal,
                  isPushedToDataServer: false,
                  metadata: { ...(cfg.metadata || {}), defaultValue: savedVal },
                }
              : cfg
          )
        );
        setLastSaved((prev) => {
          const next = [...prev];
          for (const cid of savedIds) {
            const idx = configs.findIndex((cfg) => cfg.id === cid);
            if (idx !== -1) next[idx] = savedVal;
          }
          return next;
        });
        setPendingEdits((prev) => {
          const next = { ...prev };
          for (const cid of savedIds) delete next[cid];
          return next;
        });
      } else {
        setSaveError(`Failed to save configuration: ${result.error || 'Unknown error'}`);
      }
    } catch (e) {
      setSaveError('Failed to save configuration. Please try again.');
    } finally {
      setEditingId(null);
      setEditingValue('');
    }
  };

  const handleOpenViewOverride = async (cfg: IngestionConfig) => {
    setViewOverrideConfig(cfg);
    setViewOverrideOpen(true);
    setViewOverrideLoading(true);
    setViewOverrideError(null);
    setViewOverrideListFromApi([]);

    if (!pilotIdForApi) {
      setViewOverrideLoading(false);
      setViewOverrideError('No pilot selected.');
      return;
    }

    const response = await PilotApis.getConfigOverrides(rc, pilotIdForApi, {
      config_key: cfg.configKey,
      config_type: cfg.configType ?? undefined
    });

    setViewOverrideLoading(false);
    if (response.success) {
      setViewOverrideListFromApi(response.data ?? []);
      if (response.error) setViewOverrideError(null);
    } else {
      setViewOverrideError(response.error ?? 'Failed to load overrides.');
      setViewOverrideListFromApi([]);
    }
  };

  const handleCloseViewOverride = () => {
    setViewOverrideOpen(false);
    setViewOverrideConfig(null);
    setViewOverrideError(null);
    setViewOverrideListFromApi([]);
  };

  // Action column: open override dialog
  const handleOpenOverrideDialog = async (cfg: IngestionConfig) => {
    setOverrideConfig(cfg);
    const configCurrentLevel = getConfigLevelKind(cfg);
    const allLevelOptions: { value: OverrideTargetLevel; label: string }[] = [
      { value: 'pilot', label: 'Pilot' },
      ...(applicationOptions.length > 0 ? [{ value: 'product_type' as const, label: 'Application' }] : []),
      ...(fuelTypeOptions.length > 0 ? [{ value: 'fuel_type' as const, label: 'Fuel' }] : []),
      ...(clusters.length > 0 ? [{ value: 'cluster' as const, label: 'Cluster' }] : []),
    ];
    const overrideLevelOptions = getOverrideLevelOptions(configCurrentLevel, allLevelOptions);
    const initialLevel = overrideLevelOptions.length > 0 ? overrideLevelOptions[0].value : 'product_type';
    setOverrideLevel(initialLevel);
    setOverrideValue(String(cfg.defaultValue || ''));
    setOverrideProductType(applicationFilter[0] ?? applicationOptions[0]?.value ?? '');
    setOverrideFuelType(fuelTypeFilter[0] ?? fuelTypeOptions[0]?.value ?? '');
    setOverrideOpen(true);

    try {
      if (clusters.length === 0) {
        const loaded = await fetchClusters();
        const defaultClusterId = clusterFilter[0] || (loaded[0]?.value || '');
        setOverrideCluster(defaultClusterId);
      } else {
        setOverrideCluster(clusterFilter[0] || (clusters[0]?.value || ''));
      }
    } catch (e) {
      setOverrideCluster('');
    }
  };

  const handleCloseOverrideDialog = () => {
    setOverrideOpen(false);
    setOverrideConfig(null);
  };

  const handleSubmitOverride = async () => {
    if (!overrideConfig) return;

    if (overrideLevel === 'cluster' && !overrideCluster) {
      alert('Please select a cluster to create a cluster-level override.');
      return;
    }
    if (overrideLevel === 'product_type' && !overrideProductType?.trim()) {
      alert('Please select an application (product type) for this override.');
      return;
    }
    if (overrideLevel === 'fuel_type') {
      if (applicationOptions.length > 0 && !overrideProductType?.trim()) {
        alert('Please select an application for this fuel-level override.');
        return;
      }
      if (!overrideFuelType?.trim()) {
        alert('Please select a fuel type for this override.');
        return;
      }
    }

    if (typeof overrideValue === 'string' && overrideValue.trim() === '') {
      alert('Please enter a value for the override.');
      return;
    }

    setOverrideSaving(true);
    try {
      const d = getConfigDisplay(overrideConfig);
      // cluster_id must be the cluster id from configs response (ui_filters.clusters[].id), not the display name
      const clusterIdOnly = overrideLevel === 'cluster' ? overrideCluster : undefined;
      const productTypeOnly =
        overrideLevel === 'product_type' ? overrideProductType
        : overrideLevel === 'fuel_type' ? overrideProductType
        : overrideLevel === 'cluster' ? (overrideProductType?.trim() || undefined)
        : undefined;
      const fuelTypeOnly =
        overrideLevel === 'fuel_type' ? overrideFuelType
        : overrideLevel === 'cluster' ? (overrideFuelType?.trim() || undefined)
        : undefined;
      const clusterLabel = overrideLevel === 'cluster' ? (clusters.find((c) => c.value === overrideCluster)?.label ?? overrideCluster) : '';
      const destinationLevelDisplay =
        overrideLevel === 'cluster'
          ? [clusterLabel, overrideProductType?.trim(), overrideFuelType?.trim()].filter(Boolean).join('.')
          : overrideLevel === 'product_type' ? overrideProductType
          : overrideLevel === 'fuel_type' ? overrideFuelType
          : undefined;
      const configData = [{
        project_id: pilotIdForApi,
        config_key: overrideConfig.configKey,
        config_type: overrideConfig.configType,
        config_value: overrideValue,
        config_level: overrideLevel,
        config_documentation: d.configDocumentation || '',
        config_regex: d.configRegex || '.*',
        config_data_type: d.configDataType || '',
        display_name: d.displayName,
        features: d.features || 'ingestion',
        editable: true,
        is_required: true,
        cluster_id: clusterIdOnly,
        ...(productTypeOnly ? { product_type: productTypeOnly } : {}),
        ...(fuelTypeOnly ? { fuel_type: fuelTypeOnly } : {}),
        ...(destinationLevelDisplay != null && destinationLevelDisplay !== '' ? { level_display: destinationLevelDisplay } : {}),
        source: 'override',
        ...(destinationLevelDisplay ? { source_name: destinationLevelDisplay } : {}),
      }];

      const result = await PilotApis.saveProjectConfigs(rc, configData as any);
      if (result.success) {
        await fetchConfigs(GET_ALL_CONFIGS_PARAMS);
        handleCloseOverrideDialog();
        toast.success('Override added successfully.');
      } else {
        toast.error('Failed to add override: ' + (result.error || 'Unknown error'));
      }
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleValueKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const valueFromInput =
        event.target && typeof (event.target as HTMLInputElement).value === 'string'
          ? (event.target as HTMLInputElement).value
          : undefined;
      handleSaveEditing(valueFromInput);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEditing();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      // Same "current value" as display: pending edit > lastSaved (per index) > config default. Only send configs that actually changed.
      const modifiedConfigs: Array<{ cfg: IngestionConfig; index: number; currentVal: string }> = [];
      configs.forEach((cfg, index) => {
        const d = getConfigDisplay(cfg);
        if (!d.editable || isGlobalLevelConfig(cfg)) return;
        const savedVal = lastSaved[index] ?? '';
        const currentVal = String(
          pendingEdits[cfg.id] ?? lastSaved[index] ?? d.defaultValue ?? cfg.defaultValue ?? ''
        );
        if (currentVal === savedVal) return;
        modifiedConfigs.push({ cfg, index, currentVal });
      });

      if (modifiedConfigs.length === 0) {
        alert('No changes to save!');
        return;
      }

      // Same API and payload shape as inline save (saveProjectConfigs); only changed configs are sent.
      const configsData = modifiedConfigs.map(({ cfg, currentVal }) =>
        buildOneSavePayload(cfg, currentVal || '')
      );

      const result = await PilotApis.saveProjectConfigs(rc, configsData);
      
      if (result.success) {
        const modifiedById = new Map(modifiedConfigs.map(({ cfg, currentVal }) => [cfg.id, String(currentVal ?? '')]));
        setConfigs((prev) =>
          prev.map((cfg) => {
            const nextVal = modifiedById.get(cfg.id);
            if (nextVal === undefined) return cfg;
            return {
              ...cfg,
              defaultValue: nextVal,
              isPushedToDataServer: false,
              metadata: { ...(cfg.metadata || {}), defaultValue: nextVal },
            };
          })
        );
        setPendingEdits(prev => {
          const next = { ...prev };
          for (const { cfg } of modifiedConfigs) {
            delete next[cfg.id];
          }
          return next;
        });
        setLastSaved(prev => {
          const next = [...prev];
          for (const { cfg, index, currentVal } of modifiedConfigs) {
            if (index >= 0 && index < next.length) next[index] = currentVal;
          }
          return next;
        });
        alert(`Successfully saved ${modifiedConfigs.length} configuration(s)!`);
      } else {
        setSaveError(`Failed to save configurations: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setSaveError('Failed to save configurations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatRelatedConfigs = (relatedConfigs: any): string | React.ReactElement => {
    if (!relatedConfigs) return 'N/A';
    
    if (typeof relatedConfigs === 'string') {
      return relatedConfigs || 'N/A';
    }
    
    if (Array.isArray(relatedConfigs)) {
      return relatedConfigs.map(config => 
        typeof config === 'object' && config.configKey 
          ? `${config.configKey} (${config.configType})`
          : String(config)
      ).join(', ') || 'N/A';
    }
    
    if (typeof relatedConfigs === 'object' && relatedConfigs.configKey) {
      return (
        <span>
          <b>configType:</b> {relatedConfigs.configType} <b>configKey:</b> {relatedConfigs.configKey} 
        </span>
      );
    }
    
    return 'N/A';
  };

  const handlePageChange = (_: any, value: number) => setPage(value);

  const handleResetFilters = () => {
    setLevelFilter([]);
    setApplicationFilter([]);
    setFuelTypeFilter([]);
    setClusterFilter([]);
    setConfigTypeFilter([]);
    setConfigKeyFilter([]);
    setComponentFilter([]);
    setSearch('');
    setPage(1);
  };

  const fetchClusters = async (): Promise<Array<{ value: string; label: string; clusterReference?: string }>> => {
    try {
      if (!selectedPilot || !selectedPilot.projectId) {
        return [];
      }
      
      setClusterLoading(true);

      const response = await ClusterApis.getClusters(rc, selectedPilot.projectId);
      if (response.success && response.data) {
        const combinedClusters = response.data.combined_clusters || [];
        const ref = (cluster: any) => cluster.clusterReference ?? cluster.value ?? cluster.id ?? '';
        const loaded: Array<{ value: string; label: string; clusterReference?: string }> = combinedClusters
          .map((cluster: any) => ({
            value: cluster.clusterId ?? cluster.id ?? ref(cluster),
            label: cluster.clusterReferenceName ?? cluster.clusterName ?? cluster.label ?? ref(cluster) ?? 'Unknown Cluster',
            clusterReference: ref(cluster),
          }))
          .filter((c) => Boolean(c.value) && c.value !== 'DEFAULT' && Boolean(c.label));
        setClusters(loaded);
        return loaded;
      } else {
        setClusters([]);
        return [];
      }
    } catch (error) {
      setClusters([]);
      return [];
    } finally {
      setClusterLoading(false);
    }
  };

  useEffect(() => {
    // When ui_filters is present, clusters come from configs response; no separate getClusters.
    if (uiFilters != null) return;
    const hasClusterLevel = levelFilter.some((l) => l.startsWith('CLUSTER'));
    if (hasClusterLevel) {
      fetchClusters();
    } else {
      // Only clear when there is something to clear; avoid setClusterFilter([]) when already []
      // so we don't trigger a spurious re-render (new array reference) and thus an extra fetch effect run.
      if (clusterFilter.length > 0) setClusterFilter([]);
    }
  }, [levelFilter, selectedPilotId, uiFilters]);

  return (
    <Box sx={{ 
      bgcolor: theme.palette.background.default, 
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, pt: 1.5, pb: 0, px: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : (error as { message?: string })?.message ?? 'An error occurred'}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        <Card sx={{ 
          p: 0, 
          borderRadius: 3, 
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
         }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 1.5, pb: 1.5, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <ChecklistIcon color="primary" sx={{ fontSize: 28, marginRight: 1 }} />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ textAlign: 'left', color: theme.palette.text.primary, fontSize: '1.125rem' }}
                  >
                    {clusterFilter.length > 0 ? 'Project Configs (filtered by cluster)' : 'Project Configs'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8125rem' }}>
                  Browse, edit, or add project-specific configurations.
                  {clusterFilter.length > 0 && ` Clusters: ${clusterFilter.map((id) => clusters.find((c) => c.value === id)?.label ?? id).join(', ')}`}
                  {' '}Displaying <strong>{filteredConfigs.length}</strong> of {configs.length} configurations.
                </Typography>
                {configs.some((c) => (c?.source ?? (c as any)?.source) === 'registry') && (
                  <Alert severity="info" sx={{ mt: 1, py: 0.75, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" component="span" sx={{ color: 'inherit' }}>
                      New configs are added in registry—review them in the registry if they are needed for your project.
                    </Typography>
                  </Alert>
                )}
            </Box>
            <Box sx={{display:"flex",flexDirection:"row"}}>
              <PermissionGate require={UI_PAGES.PROJECT_CONFIGS.ctas.save.id} mode="disable">
                <BglPrimaryButton
                  startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  size="small"
                  onClick={handleSave}
                  disabled={!isDirty || isSaving}
                  sx={{
                     textTransform: 'capitalize'
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save All Updates'}
                </BglPrimaryButton>
              </PermissionGate>
            </Box>
          </Box>
           
          <CardContent sx={{ pt: 0, px: 2.5, position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', paddingBottom: '0 !important', '&:last-child': { paddingBottom: '0 !important' } }}>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 1.5, overflow: 'visible', minWidth: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search configurations..."
                  value={search}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSearch(next);
                    setPage(1);
                  }}
                  inputProps={{ 'aria-label': 'Search configurations by key, name, type, component, level' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { 
                      bgcolor: theme.palette.background.paper, 
                      borderRadius: 2 
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
                <Box sx={{ flexShrink: 0, overflow: 'visible' }}>
                  <ProjectConfigFilter
                    filterOptions={projectFilterOptions}
                    levelFilter={levelFilter}
                    clusterFilter={clusterFilter}
                    applicationFilter={applicationFilter}
                    fuelTypeFilter={fuelTypeFilter}
                    effectiveApiScope={effectiveApiScope}
                    configTypes={configTypeFilter}
                    configKeyFilter={configKeyFilter}
                    componentFilter={componentFilter}
                    onLevelFilterChange={(v) => {
                      const next = v ?? [];
                      setLevelFilter(next);
                      setPage(1);
                      if (!next.some((l) => l.startsWith('CLUSTER'))) setClusterFilter([]);
                      if (!next.some((l) => l.toUpperCase() === 'APPLICATIONS')) {
                        setApplicationFilter([]);
                        setFuelTypeFilter([]);
                      }
                      if (!next.some((l) => l.toUpperCase() === 'FUEL')) setFuelTypeFilter([]);
                    }}
                    onClusterFilterChange={(v) => {
                      setClusterFilter(v);
                      setPage(1);
                    }}
                    onApplicationFilterChange={(v) => {
                      setApplicationFilter(v);
                      if ((v?.length ?? 0) === 0) {
                        setFuelTypeFilter([]);
                        setLevelFilter((prev) => prev.filter((l) => l.toUpperCase() !== 'FUEL'));
                      }
                      setPage(1);
                    }}
                    onFuelTypeFilterChange={(v) => {
                      setFuelTypeFilter(v);
                      setPage(1);
                    }}
                    onConfigTypesChange={(v) => {
                      setConfigTypeFilter(v);
                      setPage(1);
                    }}
                    onConfigKeyFilterChange={(v) => {
                      setConfigKeyFilter(v);
                      setPage(1);
                    }}
                    onComponentFilterChange={(v) => {
                      setComponentFilter(v);
                      setPage(1);
                    }}
                    onResetAll={handleResetFilters}
                    onPageReset={() => setPage(1)}
                    clusterLoading={clusterLoading}
                    includeConfigType={true}
                    activeFiltersBelow={true}
                  />
                </Box>
              </Box>
              {!applicationScopingAvailable && configs.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Application scoping isn't available for this project. Showing all configurations.
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                <ProjectConfigFilterActiveChips
                  filterOptions={projectFilterOptions}
                  levelFilter={levelFilter}
                  clusterFilter={clusterFilter}
                  applicationFilter={applicationFilter}
                  fuelTypeFilter={fuelTypeFilter}
                  effectiveApiScope={effectiveApiScope}
                  configTypes={configTypeFilter}
                  configKeyFilter={configKeyFilter}
                  componentFilter={componentFilter}
                  onLevelFilterChange={(v) => {
                    const next = v ?? [];
                    setLevelFilter(next);
                    setPage(1);
                    if (!next.some((l) => l.startsWith('CLUSTER'))) setClusterFilter([]);
                    if (!next.some((l) => l.toUpperCase() === 'APPLICATIONS')) {
                      setApplicationFilter([]);
                      setFuelTypeFilter([]);
                    }
                    if (!next.some((l) => l.toUpperCase() === 'FUEL')) setFuelTypeFilter([]);
                  }}
                  onClusterFilterChange={(v) => { setClusterFilter(v); setPage(1); }}
                  onApplicationFilterChange={(v) => {
                    setApplicationFilter(v);
                    if ((v?.length ?? 0) === 0) {
                      setFuelTypeFilter([]);
                      setLevelFilter((prev) => prev.filter((l) => l.toUpperCase() !== 'FUEL'));
                    }
                    setPage(1);
                  }}
                  onFuelTypeFilterChange={(v) => { setFuelTypeFilter(v); setPage(1); }}
                  onConfigTypesChange={(v) => { setConfigTypeFilter(v); setPage(1); }}
                  onConfigKeyFilterChange={(v) => { setConfigKeyFilter(v); setPage(1); }}
                  onComponentFilterChange={(v) => { setComponentFilter(v); setPage(1); }}
                  onResetAll={handleResetFilters}
                  onPageReset={() => setPage(1)}
                  clusterLoading={clusterLoading}
                  includeConfigType={true}
                />
              </Box>
            </Box>
            <TableContainer component={Paper} sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: 2,
              boxShadow: 0,
              mb: 0,
              overflow: 'auto',
            }}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, padding: '6px 10px', whiteSpace: 'nowrap', width: '13%' }}>
                      <TableSortLabel
                        active={sortBy === 'configType'}
                        direction={sortBy === 'configType' ? sortDirection : 'asc'}
                        onClick={() => handleSort('configType')}
                      >
                        Config Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, padding: '6px 10px', whiteSpace: 'nowrap', width: '20%' }}>
                      <TableSortLabel
                        active={sortBy === 'configKey'}
                        direction={sortBy === 'configKey' ? sortDirection : 'asc'}
                        onClick={() => handleSort('configKey')}
                      >
                        Config Key
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, padding: '6px 10px', whiteSpace: 'nowrap', width: '13%' }}>
                      Display Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, padding: '6px 10px', whiteSpace: 'nowrap', width: '9%' }}>
                      <TableSortLabel
                        active={sortBy === 'component'}
                        direction={sortBy === 'component' ? sortDirection : 'asc'}
                        onClick={() => handleSort('component')}
                      >
                        Component
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, padding: '6px 10px', whiteSpace: 'nowrap', width: '9%' }}>
                      <TableSortLabel
                        active={sortBy === 'configLevel'}
                        direction={sortBy === 'configLevel' ? sortDirection : 'asc'}
                        onClick={() => handleSort('configLevel')}
                      >
                        Config Level
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, padding: '6px 10px', width: '22%' }}>
                      Config Value
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: theme.palette.text.primary, textAlign: 'center', padding: '6px 10px', width: '14%' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(paginatedConfigs) && paginatedConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 2, px: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {search.trim()
                          ? 'No configurations match your search. Clear the search box or try different terms.'
                          : 'No configurations to display.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                  Array.isArray(paginatedConfigs) && paginatedConfigs.map((cfg, index) => {
                    if (!cfg || typeof cfg !== 'object') return null;
                    const d = getConfigDisplay(cfg);
                    const levelD = getLevelDisplayForConfig(cfg, clusters);
                    const isGlobal = isGlobalLevelConfig(cfg);
                    const valueEditable = d.editable && !isGlobal;
                    const duplicateTooltipRefs = parseDuplicateRefsFromMetadata(cfg.metadata as Record<string, unknown> | undefined);
                    const duplicateTooltipSiblingIds = duplicateClusterOthersById.get(cfg.id) ?? [];
                    const duplicateTooltipSiblings = duplicateTooltipSiblingIds
                      .map((id) => configs.find((c) => c.id === id))
                      .filter((c): c is IngestionConfig => c != null);
                    const hasDuplicateTooltip = duplicateTooltipRefs.length > 0 || duplicateTooltipSiblings.length > 0;
                    const duplicateTooltipUnresolved = duplicateTooltipRefs.filter(
                      (r) =>
                        !duplicateTooltipSiblings.some(
                          (drow) => drow.configKey === r.configKey && drow.configType === r.configType
                        )
                    );
                    const isPendingPushRow = cfg.isPushedToDataServer === false;
                    return (
                      <TableRow
                        key={cfg.id || `config-${index}`}
                        sx={{
                          bgcolor: valueEditable
                            ? theme.palette.background.paper
                            : theme.palette.background.default,
                          ...(isPendingPushRow ? {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                            boxShadow: `inset 0 0 0 2px ${theme.palette.success.light}`
                          } : {}),
                          ...(() => {
                            const cfgIndex = configs.findIndex(c => c.id === cfg.id);
                            const current = pendingEdits[cfg.id];
                            const savedVal = cfgIndex >= 0 ? (lastSaved[cfgIndex] || '') : String(cfg.defaultValue || '');
                            if (current !== undefined && current !== savedVal) {
                              return {
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.10)' : 'rgba(255, 213, 79, 0.10)',
                                boxShadow: `inset 0 0 0 2px ${theme.palette.warning.light}`
                              };
                            }
                            return {};
                          })(),
                          '&:hover': valueEditable
                            ? { bgcolor: theme.palette.action.hover }
                            : undefined
                        }}
                      >
                        <TableCell sx={{ color: theme.palette.text.primary, padding: '6px 10px', fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                          <Tooltip title={String(cfg.configType || 'N/A')} placement="top" arrow enterDelay={300}>
                            <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {String(cfg.configType || 'N/A')}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary, padding: '6px 10px', fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                            <Tooltip title={String(cfg.configKey || 'N/A')} placement="top" arrow enterDelay={300}>
                              <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', minWidth: 0 }}>
                                {String(cfg.configKey || 'N/A')}
                              </Typography>
                            </Tooltip>
                            <Tooltip
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 360,
                                    borderRadius: 2.5,
                                    px: 2,
                                    py: 1.5,
                                    boxShadow: theme.shadows[8],
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  },
                                },
                              }}
                              title={
                                <Box sx={{ display: 'block', maxWidth: 320 }}>
                                  {d.configDocumentation ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Documentation
                                      </Typography>
                                      <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
                                        {d.configDocumentation}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {hasDuplicateTooltip ? (
                                    <Box
                                      sx={{
                                        mb: 1.5,
                                        pt: d.configDocumentation ? 1.5 : 0,
                                        borderTop: d.configDocumentation ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                        Duplicate configs
                                      </Typography>
                                      
                                      {duplicateTooltipSiblings.map((drow) => (
                                        <Typography key={drow.id} variant="body2" sx={{ color: 'text.primary', display: 'block', mb: 0.5 }}>
                                          <Box component="span" sx={{ fontFamily: 'monospace' }}>{drow.configKey}</Box>
                                          {' · '}
                                          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{drow.configType}</Box>
                                          {' · '}
                                          {getLevelDisplayForConfig(drow, clusters)}
                                        </Typography>
                                      ))}
                                      {duplicateTooltipUnresolved.map((r, ui) => (
                                        <Typography key={`dup-unresolved-${ui}`} variant="body2" sx={{ color: 'warning.main', display: 'block', mb: 0.25 }}>
                                          {r.configKey} ({r.configType}) — not in current list (check filters / scope)
                                        </Typography>
                                      ))}
                                    </Box>
                                  ) : null}
                                  {(cfg as any).isEnvConfig != null ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        isEnvConfig
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {String((cfg as any).isEnvConfig)}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {d.defaultValue !== '' ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Default Value
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                                        {d.defaultValue}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {d.possibleValues ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Possible Values
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontStyle: 'italic' }}>
                                        {typeof d.possibleValues === 'string' ? d.possibleValues : JSON.stringify(d.possibleValues)}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {d.configDataType ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Config Data Type
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {d.configDataType}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {cfg.editedBy ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Last edited by
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {String(cfg.editedBy)}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {cfg.createdBy ? (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                                        Created by
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {String(cfg.createdBy)}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {!d.configDocumentation && !hasDuplicateTooltip && (cfg as any).isEnvConfig == null && d.defaultValue === '' && !d.possibleValues && !d.configDataType && !cfg.editedBy && !cfg.createdBy ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      No additional info
                                    </Typography>
                                  ) : null}
                                </Box>
                              }
                              placement="top"
                              enterDelay={300}
                              arrow
                            >
                              <span style={{ display: 'inline-flex', cursor: 'help' }} aria-label="Config key info">
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active' }} />
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary, padding: '6px 10px', fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                          <Tooltip title={String(d.displayName || 'N/A')} placement="top" arrow enterDelay={300}>
                            <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {String(d.displayName || 'N/A')}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary, padding: '6px 10px', fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                          <Tooltip title={String(d.component || 'N/A')} placement="top" arrow enterDelay={300}>
                            <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {String(d.component || 'N/A')}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary, padding: '6px 10px', fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                          <Tooltip title={levelD || 'N/A'} placement="top" arrow enterDelay={300}>
                            <Box sx={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
                              <Chip size="small" label={levelD || 'N/A'} variant="outlined" sx={{ textTransform: 'none', fontSize: '0.75rem', maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ padding: '6px 10px', fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                          {isGlobal ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.25 }}>
                              <Tooltip title={String((d.defaultValue ?? cfg.defaultValue) || '')} placement="top" arrow enterDelay={300}>
                                <Box
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: theme.typography.body2.fontSize,
                                    color: theme.palette.text.primary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {String((d.defaultValue ?? cfg.defaultValue) || '—')}
                                </Box>
                              </Tooltip>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, display: 'block' }}>
                                {GLOBAL_CONFIG_NON_EDITABLE_HINT}
                              </Typography>
                            </Box>
                          ) : d.editable ? (
                            editingId === cfg.id ? (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <TextField
                                  inputRef={editingInputRef}
                                  value={editingValue}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                    const nextVal = e.target.value;
                                    setEditingValue(nextVal);
                                    editingValueRef.current = nextVal;
                                    const syncIds = [editingId as string, ...(duplicateClusterOthersById.get(editingId as string) ?? [])];
                                    setPendingEdits((prev) => {
                                      const next = { ...prev };
                                      for (const id of syncIds) next[id] = nextVal;
                                      return next;
                                    });
                                  }}
                                  onKeyDown={handleValueKeyDown as React.KeyboardEventHandler<HTMLDivElement>}
                                  autoFocus
                                  multiline
                                  minRows={1}
                                  fullWidth
                                  size="small"
                                   sx={{
                                     maxWidth: '100%',
                                     '& .MuiOutlinedInput-root': {
                                       color: theme.palette.text.primary,
                                       borderRadius: 1,
                                       '& fieldset': { borderColor: theme.palette.divider }
                                     },
                                     '& .MuiOutlinedInput-input': {
                                       fontFamily: 'monospace',
                                       fontSize: theme.typography.body2.fontSize
                                     }
                                   }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Tooltip title="Save (Enter)" arrow>
                                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSaveEditing()}>
                                      <SaveIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel (Esc)" arrow>
                                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleCancelEditing}>
                                      <CancelIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            ) : (
                              (() => {
                                const cfgIndex = configs.findIndex(c => c.id === cfg.id);
                                const displayVal = pendingEdits[cfg.id] ?? (cfgIndex >= 0 ? lastSaved[cfgIndex] : undefined) ?? d.defaultValue ?? cfg.defaultValue;
                                return (
                              <Box 
                                onClick={() => handleStartEditing(cfg)}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  px: 1,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: theme.palette.action.hover },
                                  '&:hover .editIcon': { opacity: 1 }
                                }}
                              >
                                <Tooltip title={String(displayVal || '')} placement="top" arrow>
                                  <Box sx={{
                                    fontFamily: 'monospace',
                                    fontSize: theme.typography.body2.fontSize,
                                    color: theme.palette.text.primary,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                    maxWidth: '100%'
                                  }}>
                                    {String(displayVal || 'empty')}
                                  </Box>
                                </Tooltip>
                                <IconButton size="small" className="editIcon" sx={{ opacity: 0 }}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Box>
                                );
                              })() )
                            ) : (
                            <Tooltip title={String((d.defaultValue ?? cfg.defaultValue) || 'N/A')} placement="top" arrow enterDelay={300}>
                              <Box sx={{ 
                                color: theme.palette.text.disabled, 
                                fontStyle: 'italic', 
                                px: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}>
                                {String((d.defaultValue ?? cfg.defaultValue) || 'N/A')}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', padding: '6px 10px', fontSize: '0.8125rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                            <Tooltip title="View Overrides" arrow>
                              <IconButton size="small" onClick={() => handleOpenViewOverride(cfg)} aria-label="View overrides">
                                <VisibilityOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Override" arrow>
                              <IconButton size="small" onClick={() => handleOpenOverrideDialog(cfg)} aria-label="Add override">
                                <AddCircleOutlineOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                // Match ConfigComparePage difference table footer: spacing + comfortable padding
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mt: 2,
                py: 1.5,
                px: 0,
                flexShrink: 0,
                width: '100%',
                background: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                zIndex: 2,
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 1 auto', minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  Showing {filteredConfigs.length === 0 ? 0 : (page - 1) * rowsPerPage + 1} – {Math.min(page * rowsPerPage, filteredConfigs.length)} of {filteredConfigs.length} configurations
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.8125rem' }}>Rows:</Typography>
                  <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                    sx={{ minWidth: 70, height: 32 }}
                  >
                    {ROWS_PER_PAGE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', flex: '0 0 auto' }}>
                <BglPrimaryOutlinedSmallButton
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ height: 32, minWidth: 48, padding: '0 12px' }}
                >
                  Prev
                </BglPrimaryOutlinedSmallButton>
                {(() => {
                  const totalPages = Math.max(1, Math.ceil(filteredConfigs.length / rowsPerPage));
                  const start = totalPages <= 10 ? 1 : Math.max(1, Math.min(page - 5, totalPages - 9));
                  const buttonSx = { minWidth: 36, height: 32, padding: 0 };
                  return Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                    const pageNum = start + i;
                    if (pageNum > totalPages) return null;
                    return page === pageNum ? (
                      <BglPrimarySmallButton
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={buttonSx}
                      >
                        {pageNum}
                      </BglPrimarySmallButton>
                    ) : (
                      <BglPrimaryOutlinedSmallButton
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        sx={buttonSx}
                      >
                        {pageNum}
                      </BglPrimaryOutlinedSmallButton>
                    );
                  });
                })()}
                <BglPrimaryOutlinedSmallButton
                  disabled={page >= Math.max(1, Math.ceil(filteredConfigs.length / rowsPerPage)) || filteredConfigs.length === 0}
                  onClick={() => setPage((p) => Math.min(Math.max(1, Math.ceil(filteredConfigs.length / rowsPerPage)), p + 1))}
                  sx={{ height: 32, minWidth: 48, padding: '0 12px' }}
                >
                  Next
                </BglPrimaryOutlinedSmallButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Overlay Loader for Operations */}
      <BglOverlayLoader
        open={isLoading || isSaving}
        message={
          isLoading 
            ? clusterFilter.length > 0 
              ? `Loading configurations...` 
              : "Loading configurations..."
            : isSaving 
              ? "Saving configurations..." 
              : ""
        }
        size={50}
      />

      {/* View Override Modal — Studio-style, wider for table columns */}
      <StudioModal
        open={viewOverrideOpen}
        onClose={handleCloseViewOverride}
        title={`View Overrides`}
        description={
          <>
            Showing values for <strong>{viewOverrideConfig?.configKey ?? ''}</strong> at all configured levels. The most specific level takes precedence.
          </>
        }
        contentStyle={{ maxWidth: 920 }}
        footer={
          <>
            <button
              type="button"
              className={`${studioStyles.button} ${studioStyles.primaryButton}`}
              onClick={handleCloseViewOverride}
            >
              <CloseIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'middle' }} />
              Close
            </button>
            {viewOverrideConfig && (
              <button
                type="button"
                className={`${studioStyles.button} ${studioStyles.outlineButton}`}
                onClick={() => { handleCloseViewOverride(); handleOpenOverrideDialog(viewOverrideConfig); }}
              >
                <AddCircleOutlineOutlinedIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'middle' }} />
                Add Override
              </button>
            )}
          </>
        }
      >
        {viewOverrideLoading ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Loading overrides…
          </Typography>
        ) : viewOverrideError ? (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            {viewOverrideError}
          </Typography>
        ) : viewOverrideListFromApi.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No overrides found. Use &quot;Override&quot; to add a new level/cluster value.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                    <LabelOutlinedIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {' Level'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', minWidth: 120 }}>
                    <CodeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {' Value'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', minWidth: 140 }}>
                    <ScheduleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {' Date & time'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                    <PersonOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {' Created by'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewOverrideListFromApi.map((c, idx) => {
                  const d = getConfigDisplay(c);
                  const levelD = getLevelDisplayForConfig(c, clusters);
                  const levelLabel =
                    levelD ||
                    (c.configLevel === 'cluster' && c.clusterReference
                      ? `Cluster · ${clusters.find((x) => x.value === c.clusterReference)?.label ?? c.clusterReference}`
                      : c.configLevel === 'product_type' && (c.productType ?? (c as any).product_type)
                        ? `Application · ${c.productType ?? (c as any).product_type}`
                        : c.configLevel === 'fuel_type' && (c.fuelType ?? (c as any).fuel_type)
                          ? `Fuel · ${c.fuelType ?? (c as any).fuel_type}`
                          : (c.level_display ?? d.levelDisplay ?? c.configLevel ?? c.level ?? '—'));
                  const dateTimeRaw = c.updatedAt ?? c.createdAt ?? (c as any).datetime ?? (c as any).updated_at ?? (c as any).created_at;
                  const dateTime =
                    dateTimeRaw
                      ? (() => {
                          try {
                            const date = new Date(dateTimeRaw);
                            return isNaN(date.getTime()) ? dateTimeRaw : date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
                          } catch {
                            return dateTimeRaw;
                          }
                        })()
                      : '—';
                  const overrideValue = String((d.defaultValue ?? c.defaultValue ?? (c as any).value) ?? '') || '—';
                  const createdByDisplay = c.createdBy ?? (c as any).created_by ?? (c as any).createdby ?? '—';
                  return (
                    <TableRow key={c.id ?? `vo-${idx}`}>
                      <TableCell sx={{ fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                        <Tooltip title={levelLabel} placement="top" arrow enterDelay={300}>
                          <Box sx={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
                            <Chip size="small" label={levelLabel} variant="outlined" sx={{ textTransform: 'none', fontSize: '0.75rem', maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', overflow: 'hidden', maxWidth: 0 }}>
                        <Tooltip title={overrideValue} placement="top" arrow enterDelay={300}>
                          <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {overrideValue}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 0 }}>
                        <Tooltip title={dateTime} placement="top" arrow enterDelay={300}>
                          <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {dateTime}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', overflow: 'hidden', maxWidth: 0 }}>
                        <Tooltip title={String(createdByDisplay)} placement="top" arrow enterDelay={300}>
                          <Box component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {createdByDisplay}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </StudioModal>

      {/* Override Configuration Modal — Studio-style */}
      <StudioModal
        open={overrideOpen}
        onClose={handleCloseOverrideDialog}
        title={`Override Configuration${clusterFilter.length > 0 ? ` — Cluster: ${clusters.find((c) => c.value === clusterFilter[0])?.label ?? clusterFilter[0]}` : ''}`}
        description={overrideConfig ? `Create a new, more specific configuration for the key ${overrideConfig.configKey}.` : undefined}
        footer={
          <>
            <button
              type="button"
              className={`${studioStyles.button} ${studioStyles.outlineButton}`}
              onClick={handleCloseOverrideDialog}
              disabled={overrideSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${studioStyles.button} ${studioStyles.primaryButton}`}
              onClick={handleSubmitOverride}
              disabled={
                overrideSaving ||
                (overrideLevel === 'cluster' && !overrideCluster) ||
                (overrideLevel === 'product_type' && !overrideProductType?.trim()) ||
                (overrideLevel === 'fuel_type' && (applicationOptions.length > 0 && !overrideProductType?.trim() || !overrideFuelType?.trim())) ||
                (typeof overrideValue === 'string' && overrideValue.trim() === '')
              }
            >
              {overrideSaving ? 'Adding...' : 'Add Config'}
            </button>
          </>
        }
      >
        {overrideConfig && (
          <>
            <Box sx={{ p: 2, mb: 2, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Config Key</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{overrideConfig.configKey}</Typography>
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>Display Name</Typography>
                <Typography variant="body2">{overrideConfig.displayName}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>New Level</Typography>
                <BglSelect
                  value={overrideLevel}
                  onChange={async (v) => {
                    const nextLevel = v as OverrideTargetLevel;
                    setOverrideLevel(nextLevel);
                    if (nextLevel === 'cluster' && !overrideCluster) {
                      const loaded = clusters.length === 0 ? await fetchClusters() : clusters;
                      const defaultClusterId = clusterFilter[0] || (loaded[0]?.value || '');
                      setOverrideCluster(defaultClusterId);
                    }
                    if (nextLevel === 'product_type' && !overrideProductType && applicationOptions.length > 0) {
                      setOverrideProductType(applicationFilter[0] ?? applicationOptions[0]?.value ?? '');
                    }
                    if (nextLevel === 'fuel_type') {
                      if (!overrideFuelType && fuelTypeOptions.length > 0) {
                        setOverrideFuelType(fuelTypeFilter[0] ?? fuelTypeOptions[0]?.value ?? '');
                      }
                      if (applicationOptions.length > 0 && !overrideProductType) {
                        setOverrideProductType(applicationFilter[0] ?? applicationOptions[0]?.value ?? '');
                      }
                    }
                  }}
                  options={(() => {
                    const configCurrentLevel = overrideConfig ? getConfigLevelKind(overrideConfig) : null;
                    const allOptions: { value: OverrideTargetLevel; label: string }[] = [
                      { value: 'pilot', label: 'Pilot' },
                      ...(applicationOptions.length > 0 ? [{ value: 'product_type' as const, label: 'Application' }] : []),
                      ...(fuelTypeOptions.length > 0 ? [{ value: 'fuel_type' as const, label: 'Fuel' }] : []),
                      ...(clusters.length > 0 ? [{ value: 'cluster' as const, label: 'Cluster' }] : []),
                    ];
                    return configCurrentLevel != null
                      ? getOverrideLevelOptions(configCurrentLevel, allOptions)
                      : allOptions;
                  })()}
                  placeholder="Select level"
                  minWidth={180}
                  inputPaddingY={0.7}
                />
              </Box>
              {overrideLevel === 'cluster' && (
                <Box sx={{ minWidth: 260, flex: '1 1 260px' }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>Cluster</Typography>
                  <BglSelect
                    value={overrideCluster}
                    onChange={(v) => setOverrideCluster(String(v))}
                    options={clusters}
                    placeholder={clusterLoading ? 'Loading clusters…' : 'Select cluster'}
                    minWidth={180}
                    loading={clusterLoading}
                    sort
                    inputPaddingY={0.7}
                  />
                </Box>
              )}
              {(overrideLevel === 'product_type' || overrideLevel === 'fuel_type' || overrideLevel === 'cluster') && applicationOptions.length > 0 && (
                <Box sx={{ minWidth: 260, flex: '1 1 260px' }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
                    Application{overrideLevel === 'cluster' ? ' (optional)' : ''}
                  </Typography>
                  <BglSelect
                    value={overrideProductType}
                    onChange={(v) => setOverrideProductType(String(v))}
                    options={applicationOptions}
                    placeholder={overrideLevel === 'cluster' ? 'None' : 'Select application'}
                    minWidth={180}
                    inputPaddingY={0.7}
                  />
                </Box>
              )}
              {(overrideLevel === 'fuel_type' || overrideLevel === 'cluster') && fuelTypeOptions.length > 0 && (
                <Box sx={{ minWidth: 260, flex: '1 1 260px' }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
                    Fuel Type{overrideLevel === 'cluster' ? ' (optional)' : ''}
                  </Typography>
                  <BglSelect
                    value={overrideFuelType}
                    onChange={(v) => setOverrideFuelType(String(v))}
                    options={fuelTypeOptions}
                    placeholder={overrideLevel === 'cluster' ? 'None' : 'Select fuel type'}
                    minWidth={180}
                    inputPaddingY={0.7}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>New Value</Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={overrideValue}
                onChange={(e) => setOverrideValue(e.target.value)}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Box>
          </>
        )}
      </StudioModal>
    </Box>
  );
}; 
